import { useCallback, useEffect, useRef } from 'react';

/*
  Whether a paste or drop can still land in the message it was made in.

  Its pictures arrive a moment after it: each is sized to send first (normalizeImage), a few hundred
  milliseconds for a screenshot and longer for several large ones. A Batch Send shows every row's
  message in the same editor and the same SMS box, re-pointed at whichever row is open, so a sender
  who moved to another row in that time had the pictures land in the row they moved to - or in a
  hidden copy of the old one that was thrown away.

  contentKey names the message on screen (the Batch Send's open row). It is noted at the paste and
  compared when the pictures are ready: a different key, or a field that has since gone (the row
  pane unmounts on the Default message), means the paste is not added - see reportNotAdded. With no
  key - every other compose window, and the Batch Sends' own Default message, which never changes
  underneath its editor - a paste always lands, as it always has.

  Returns notePasteTarget: call it at the paste, and call what it returns once the pictures are
  ready.
*/
export default function usePasteTarget(contentKey?: string) {
  // Read when the pictures land, so it is the key of that moment rather than of a render.
  const keyRef = useRef(contentKey);
  keyRef.current = contentKey;

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(() => {
    const pastedInto = keyRef.current;
    return () => pastedInto === undefined || (mountedRef.current && keyRef.current === pastedInto);
  }, []);
}
